// PLAYBACK 制御 UI。日付選択 / speed / Pause / LIVE / Restart / ジャンプ / シークバー。
// engine がモード (idle/playback/live) を持ち、ここはその薄い操作 + 状態表示レイヤ。

import { SEEK_MAX } from './config.js';
import { requireElementById } from './utils.js';
import { todayJstDateStr } from './utils.js';
import { PlaybackEngine } from './engine.js';

export function installPlaybackUi(engine: PlaybackEngine): void {
  const playbackEl = requireElementById('playback');
  const playbackStatusEl = requireElementById('playbackStatus');
  const seekRaw = requireElementById('playbackSeek');
  if (!(seekRaw instanceof HTMLInputElement)) {
    throw new Error('#playbackSeek must be an <input>');
  }
  const seekEl: HTMLInputElement = seekRaw;
  const seekWrap = requireElementById('seekWrap');
  const seekBuffered = requireElementById('seekBuffered');
  const seekProgress = requireElementById('seekProgress');
  const seekTooltip = requireElementById('seekTooltip');
  const dateRaw = requireElementById('playbackDate');
  if (!(dateRaw instanceof HTMLInputElement)) {
    throw new Error('#playbackDate must be an <input>');
  }
  const dateEl: HTMLInputElement = dateRaw;
  let seekDragging = false;

  // 今日まで選べる
  dateEl.max = todayJstDateStr();

  function cursorFromSeekValue(): number | null {
    const first = engine.getFirstAtMs();
    const last = engine.getLastAtMs();
    if (first === null || last === null || last <= first) return null;
    const v = Number(seekEl.value);
    if (!Number.isFinite(v)) return null;
    return first + ((last - first) * v) / SEEK_MAX;
  }

  function fmtHmsTime(ms: number): string {
    const d = new Date(ms);
    const pad = (n: number): string => (n < 10 ? `0${n}` : String(n));
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }

  seekEl.addEventListener('pointerdown', () => {
    seekDragging = true;
  });
  seekEl.addEventListener('input', () => {
    seekDragging = true;
    const pos = cursorFromSeekValue();
    if (pos === null) return;
    playbackStatusEl.textContent = `${fmtHmsTime(pos)} · seeking`;
  });
  seekEl.addEventListener('change', () => {
    const pos = cursorFromSeekValue();
    seekDragging = false;
    if (pos === null) return;
    void (async () => {
      await engine.seek(pos);
      updatePlaybackStatus();
    })();
  });

  // ホバー位置の時刻を tooltip 表示
  function timeAtFraction(frac: number): number | null {
    const first = engine.getFirstAtMs();
    const last = engine.getLastAtMs();
    if (first === null || last === null || last <= first) return null;
    const f = Math.max(0, Math.min(1, frac));
    return first + (last - first) * f;
  }
  seekWrap.addEventListener('pointermove', (ev) => {
    if (!(ev instanceof PointerEvent)) return;
    const rect = seekWrap.getBoundingClientRect();
    if (rect.width <= 0) return;
    const frac = (ev.clientX - rect.left) / rect.width;
    const t = timeAtFraction(frac);
    if (t === null) {
      seekTooltip.classList.add('is-hidden');
      return;
    }
    seekTooltip.classList.remove('is-hidden');
    seekTooltip.textContent = fmtHmsTime(t);
    const px = Math.max(0, Math.min(rect.width, ev.clientX - rect.left));
    seekTooltip.style.left = `${px}px`;
  });
  seekWrap.addEventListener('pointerleave', () => {
    seekTooltip.classList.add('is-hidden');
  });

  dateEl.addEventListener('change', () => {
    const v = dateEl.value;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return;
    void (async () => {
      const today = todayJstDateStr();
      const isToday = v === today;
      await engine.loadDay(v, {
        autoLive: isToday,
        ...(isToday ? {} : { autoPlaySpeed: 60 }),
      });
      updatePlaybackStatus();
    })();
  });

  playbackEl.addEventListener('click', (ev) => {
    if (!(ev.target instanceof HTMLElement)) return;
    const btn = ev.target.closest('button');
    if (!(btn instanceof HTMLButtonElement)) return;
    void handlePlaybackClick(btn);
  });

  async function handlePlaybackClick(btn: HTMLButtonElement): Promise<void> {
    if (btn.dataset['playbackPause'] !== undefined) {
      engine.setSpeed(0);
      updatePlaybackStatus();
      return;
    }
    if (btn.dataset['playbackRestart'] !== undefined) {
      await engine.restart();
      if (engine.getSpeed() === 0) engine.setSpeed(60);
      updatePlaybackStatus();
      return;
    }
    if (btn.dataset['playbackLive'] !== undefined) {
      await engine.enterLive();
      updatePlaybackStatus();
      return;
    }
    const jump = btn.dataset['playbackJump'];
    if (jump !== undefined) {
      await engine.jump(Number(jump));
      updatePlaybackStatus();
      return;
    }
    const sp = btn.dataset['playbackSpeed'];
    if (sp !== undefined) {
      if (engine.getCursorMs() === null) await engine.restart();
      engine.setSpeed(Number(sp));
      updatePlaybackStatus();
    }
  }

  function updatePlaybackStatus(): void {
    const live = engine.isLive();
    const cur = engine.getCursorMs();

    // speed ボタンのハイライト
    for (const btn of playbackEl.querySelectorAll('button[data-playback-speed]')) {
      if (!(btn instanceof HTMLElement)) continue;
      const sp = Number(btn.dataset['playbackSpeed']);
      btn.classList.toggle('is-active', !live && engine.isActive() && sp === engine.getSpeed());
    }
    // LIVE ボタンのハイライト
    for (const btn of playbackEl.querySelectorAll('button[data-playback-live]')) {
      if (!(btn instanceof HTMLElement)) continue;
      btn.classList.toggle('is-active', live);
    }

    // 日付ピッカーの値を engine に追従
    const day = engine.getLoadedDay();
    if (day !== null && dateEl.value !== day) {
      dateEl.value = day;
    }

    // シークバー: input value (= thumb 位置) と progress / buffered overlay を更新
    const first = engine.getFirstAtMs();
    const last = engine.getLastAtMs();
    if (first !== null && last !== null && last > first) {
      const span = last - first;
      if (cur !== null) {
        const cursorFrac = Math.max(0, Math.min(1, (cur - first) / span));
        if (!seekDragging) {
          seekEl.value = String(Math.round(cursorFrac * SEEK_MAX));
        }
        seekProgress.style.width = `${cursorFrac * 100}%`;
      } else {
        seekProgress.style.width = '0%';
      }
      const fetched = engine.getFetchedUptoMs();
      if (fetched !== null) {
        const fetchedFrac = Math.max(0, Math.min(1, (fetched - first) / span));
        seekBuffered.style.width = `${fetchedFrac * 100}%`;
      } else {
        seekBuffered.style.width = '0%';
      }
    }

    // ステータステキスト
    if (cur !== null && !seekDragging) {
      const time = fmtHmsTime(cur);
      const tail = live ? 'LIVE' : engine.getSpeed() === 0 ? 'paused' : `${engine.getSpeed()}x`;
      playbackStatusEl.textContent = `${time} · ${tail}`;
      playbackStatusEl.classList.toggle('is-running', engine.isActive());
      playbackStatusEl.classList.toggle('is-live', live);
    } else if (cur === null) {
      playbackStatusEl.textContent = 'idle';
      playbackStatusEl.classList.remove('is-running');
      playbackStatusEl.classList.remove('is-live');
    }
  }

  window.setInterval(updatePlaybackStatus, 250);
  void engine.init().then(() => {
    updatePlaybackStatus();
  });
}
